import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  ProcessTemplateService,
  ProcessTemplate,
  TaskTemplate
} from '../../../shared/services/process-template.service';

@Component({
  selector: 'app-process-templates',
  templateUrl: './process-templates.component.html',
  styleUrls: ['./process-templates.component.css'],
  standalone: false
})
export class ProcessTemplatesComponent implements OnInit {
  // ---- State ----
  processTemplates: ProcessTemplate[] = [];
  selectedTemplate: ProcessTemplate | null = null;
  tasks: TaskTemplate[] = [];

  loading = false;
  tasksLoading = false;

  isEditingTemplate = false;
  isNewTemplate = false;
  isEditingTask = false;
  isNewTask = false;
  selectedTask: TaskTemplate | null = null;

  // ---- Forms ----
  templateForm: FormGroup;
  taskForm: FormGroup;

  // ---- Delete confirmation ----
  deleteTemplateDialogOpen = false;
  deleteTaskDialogOpen = false;

  constructor(
    private service: ProcessTemplateService,
    private notificationService: NotificationService,
    private fb: FormBuilder
  ) {
    this.templateForm = this.fb.group({
      id: [null],
      name: ['', [Validators.required, Validators.maxLength(50)]],
      description: ['', [Validators.required, Validators.maxLength(250)]]
    });

    this.taskForm = this.fb.group({
      id: [null],
      process_Id: [null, Validators.required],
      parentProcess_Id: [null],
      sequence: [10, [Validators.required, Validators.min(1)]],
      isParallel: [false],
      template_Id: [null],
      isRequired: [true]
    });
  }

  ngOnInit(): void {
    this.loadTemplates();
  }

  // ============================================
  // Data Loading
  // ============================================

  loadTemplates(): void {
    this.loading = true;
    this.service.getProcessTemplates().subscribe({
      next: (data) => {
        this.processTemplates = data;
        this.loading = false;
      },
      error: () => {
        this.showNotification('Failed to load process templates.', 'error');
        this.loading = false;
      }
    });
  }

  loadTasks(templateId: number): void {
    this.tasksLoading = true;
    this.service.getTaskTemplates(templateId).subscribe({
      next: (data) => {
        this.tasks = data;
        this.tasksLoading = false;
      },
      error: () => {
        this.showNotification('Failed to load task templates.', 'error');
        this.tasksLoading = false;
      }
    });
  }

  // ============================================
  // Template CRUD
  // ============================================

  onTemplateSelect(e: any): void {
    this.selectedTemplate = e.selectedRows?.[0]?.dataItem ?? null;
    this.isEditingTemplate = false;
    this.isNewTemplate = false;
    this.isEditingTask = false;
    this.isNewTask = false;
    this.selectedTask = null;
    if (this.selectedTemplate?.id) {
      this.loadTasks(this.selectedTemplate.id);
    }
  }

  createNewTemplate(): void {
    this.selectedTemplate = null;
    this.isNewTemplate = true;
    this.isEditingTemplate = true;
    this.tasks = [];
    this.templateForm.reset({ name: '', description: '' });
  }

  editTemplate(): void {
    this.isEditingTemplate = true;
    this.templateForm.patchValue({
      id: this.selectedTemplate!.id,
      name: this.selectedTemplate!.name,
      description: this.selectedTemplate!.description
    });
  }

  cancelEditTemplate(): void {
    this.isEditingTemplate = false;
    if (this.isNewTemplate) {
      this.isNewTemplate = false;
      this.selectedTemplate = null;
    }
  }

  saveTemplate(): void {
    if (this.templateForm.invalid) return;
    const value: ProcessTemplate = this.templateForm.value;

    if (value.id) {
      this.service.updateProcessTemplate(value.id, value).subscribe({
        next: (updated) => {
          const idx = this.processTemplates.findIndex(t => t.id === updated.id);
          if (idx > -1) this.processTemplates[idx] = updated;
          this.processTemplates = [...this.processTemplates];
          this.selectedTemplate = updated;
          this.isEditingTemplate = false;
          this.showNotification('Process template updated.', 'success');
        },
        error: () => this.showNotification('Failed to save process template.', 'error')
      });
    } else {
      const { id, ...payload } = value;
      this.service.createProcessTemplate(payload as ProcessTemplate).subscribe({
        next: (created) => {
          this.processTemplates = [...this.processTemplates, created];
          this.selectedTemplate = created;
          this.isNewTemplate = false;
          this.isEditingTemplate = false;
          this.loadTasks(created.id!);
          this.showNotification('Process template created.', 'success');
        },
        error: () => this.showNotification('Failed to create process template.', 'error')
      });
    }
  }

  confirmDeleteTemplate(): void {
    this.deleteTemplateDialogOpen = true;
  }

  cancelDeleteTemplate(): void {
    this.deleteTemplateDialogOpen = false;
  }

  deleteTemplate(): void {
    this.deleteTemplateDialogOpen = false;
    this.service.deleteProcessTemplate(this.selectedTemplate!.id!).subscribe({
      next: () => {
        this.processTemplates = this.processTemplates.filter(t => t.id !== this.selectedTemplate!.id);
        this.selectedTemplate = null;
        this.tasks = [];
        this.showNotification('Process template deleted.', 'success');
      },
      error: () => this.showNotification('Failed to delete process template.', 'error')
    });
  }

  // ============================================
  // Task CRUD
  // ============================================

  onTaskSelect(e: any): void {
    this.selectedTask = e.selectedRows?.[0]?.dataItem ?? null;
    this.isEditingTask = false;
    this.isNewTask = false;
  }

  createNewTask(): void {
    this.isNewTask = true;
    this.isEditingTask = true;
    this.selectedTask = null;
    this.taskForm.reset({
      process_Id: null,
      parentProcess_Id: null,
      sequence: (this.tasks.length + 1) * 10,
      isParallel: false,
      template_Id: this.selectedTemplate!.id,
      isRequired: true
    });
  }

  editTask(task: TaskTemplate): void {
    this.selectedTask = task;
    this.isEditingTask = true;
    this.isNewTask = false;
    this.taskForm.patchValue(task);
  }

  cancelEditTask(): void {
    this.isEditingTask = false;
    this.isNewTask = false;
  }

  saveTask(): void {
    if (this.taskForm.invalid) return;
    const value: TaskTemplate = this.taskForm.value;
    const templateId = this.selectedTemplate!.id!;

    if (value.id) {
      this.service.updateTaskTemplate(templateId, value.id, value).subscribe({
        next: (updated) => {
          const idx = this.tasks.findIndex(t => t.id === updated.id);
          if (idx > -1) this.tasks[idx] = updated;
          this.tasks = [...this.tasks];
          this.selectedTask = updated;
          this.isEditingTask = false;
          this.showNotification('Task template updated.', 'success');
        },
        error: () => this.showNotification('Failed to save task template.', 'error')
      });
    } else {
      // Strip null id so the backend int Id doesn't fail deserialization
      const { id, ...payload } = value;
      this.service.createTaskTemplate(templateId, payload as TaskTemplate).subscribe({
        next: (created) => {
          this.tasks = [...this.tasks, created];
          this.selectedTask = created;
          this.isNewTask = false;
          this.isEditingTask = false;
          this.showNotification('Task template created.', 'success');
        },
        error: () => this.showNotification('Failed to create task template.', 'error')
      });
    }
  }

  confirmDeleteTask(task: TaskTemplate): void {
    this.selectedTask = task;
    this.deleteTaskDialogOpen = true;
  }

  cancelDeleteTask(): void {
    this.deleteTaskDialogOpen = false;
  }

  deleteTask(): void {
    this.deleteTaskDialogOpen = false;
    this.service.deleteTaskTemplate(this.selectedTemplate!.id!, this.selectedTask!.id!).subscribe({
      next: () => {
        this.tasks = this.tasks.filter(t => t.id !== this.selectedTask!.id);
        this.selectedTask = null;
        this.isEditingTask = false;
        this.showNotification('Task template deleted.', 'success');
      },
      error: () => this.showNotification('Failed to delete task template.', 'error')
    });
  }

  // ============================================
  // Helpers
  // ============================================

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: type, icon: true },
      hideAfter: 3000
    });
  }
}
